import { useState, useEffect, useCallback, type KeyboardEvent } from "react";
import { getCurrentDateISO, addDaysToDate } from "../lib/time";
import Icon from "./Icon";

interface DateRangePickerProps {
  onDateRangeChange: (startDate: string, days: number) => void;
  maxDays?: number;
  /** Controlled start date. When provided, it is the source of truth. */
  startDate?: string;
  /** Controlled duration in days. When provided, it is the source of truth. */
  days?: number;
}

export default function DateRangePicker({
  onDateRangeChange,
  maxDays = 7,
  startDate: startDateProp,
  days: daysProp,
}: DateRangePickerProps) {
  const [startDate, setStartDate] = useState(
    startDateProp ?? getCurrentDateISO()
  );
  const [days, setDays] = useState(daysProp ?? 3);
  const [isTimelineOpen, setTimelineOpen] = useState(false);

  // Synchronize down when a controlling parent supplies new values.
  useEffect(() => {
    if (startDateProp !== undefined) {
      setStartDate(startDateProp);
    }
  }, [startDateProp]);

  useEffect(() => {
    if (daysProp !== undefined) {
      setDays(daysProp);
    }
  }, [daysProp]);

  useEffect(() => {
    onDateRangeChange(startDate, days);
  }, [startDate, days, onDateRangeChange]);

  const endDate = addDaysToDate(startDate, days - 1);

  const toggleTimeline = useCallback(() => {
    setTimelineOpen((prev) => !prev);
  }, []);

  const handleTimelineKeyDown = useCallback(
    (event: KeyboardEvent<HTMLHeadingElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggleTimeline();
      }
    },
    [toggleTimeline]
  );

  const timelineContentId = "mission-timeline-content";

  return (
    <div className="hero-panel__section">
      <h2
        className="text-xl font-semibold text-primary mission-timeline-toggle"
        id="mission-timeline-toggle"
        role="button"
        tabIndex={0}
        aria-expanded={isTimelineOpen}
        aria-controls={timelineContentId}
        data-open={isTimelineOpen}
        onClick={toggleTimeline}
        onKeyDown={handleTimelineKeyDown}
      >
        <span className="inline-flex items-center gap-2">
          <Icon name="stopwatch" />
          Forecast window
        </span>
        <span
          className="mission-toggle-indicator"
          id="mission-toggle-indicator"
          aria-hidden="true"
          data-open={isTimelineOpen}
        >
          <Icon name="caret" />
        </span>
      </h2>

      <div
        className="grid gap-4 mission-timeline-content"
        id={timelineContentId}
        data-open={isTimelineOpen}
        aria-hidden={!isTimelineOpen}
      >
        <div>
          <label
            htmlFor="start-date"
            className="block text-sm font-medium mb-2"
          >
            Start date
          </label>
          <input
            id="start-date"
            type="date"
            className="input"
            value={startDate}
            min={getCurrentDateISO()}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="num-days" className="block text-sm font-medium mb-2">
            Duration: {days} Day{days !== 1 ? "s" : ""}
          </label>
          <input
            className="range-input w-full"
            id="num-days"
            type="range"
            min="1"
            max={maxDays.toString()}
            step="1"
            value={days.toString()}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            style={{
              background: `linear-gradient(to right, var(--river-green) 0%, var(--river-green) ${
                (days / maxDays) * 100
              }%, var(--border-primary) ${
                (days / maxDays) * 100
              }%, var(--border-primary) 100%)`,
            }}
          />
          <div className="flex justify-between text-sm text-muted mt-1">
            <span>1 day</span>
            <span>{maxDays} days</span>
          </div>
        </div>

        <div className="bg-accent p-3 rounded-lg border border-primary">
          <p className="text-sm text-secondary">
            <strong className="text-primary">Forecast window:</strong>{" "}
            {startDate}
            <Icon name="arrowRight" className="mx-2" />
            {endDate}
          </p>
          <p className="text-xs text-muted mt-1">
            <span className="inline-flex items-center gap-2">
              <Icon name="calendar" />
              {days}-day outlook, updated daily
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
